import { useDisclosure } from "@chakra-ui/hooks";
import { Modal, ModalOverlay } from "@chakra-ui/modal";
import { get2FTAuth, get2FTAuthVerify } from "api/graphQL_users";
import React, { useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import { MdHighlightOff } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import { setToken } from "utils/auth";

/* ═══════════════════════════════════════════════
   Deactivate 2FA — Confirmation Panel
═══════════════════════════════════════════════ */
const Deactivate2FAPanel = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => {
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const [confirmed, setConfirmed] = React.useState(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setConfirmed(false);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 320);
  };

  const handleConfirm = () => {
    if (!confirmed) return;
    onConfirm();
  };

  // Reset confirmed state when panel closes
  useEffect(() => { if (!isOpen) setConfirmed(false); }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={() => {}}>
      <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

      <div className={`d2fa-panel${isOpen && !isCloseModal ? " d2fa-panel--open" : " d2fa-panel--close"}`}>

        {/* Top glow stripe */}
        <div className="d2fa-glow" />

        {/* Header */}
        <div className="d2fa-header">
          <div className="d2fa-header-left">
            <div className="d2fa-header-icon">
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <div>
              <h2 className="d2fa-header-title">Deactivate 2FA</h2>
              <p className="d2fa-header-sub">This will reduce your account security</p>
            </div>
          </div>
          <button className="d2fa-close-btn" onClick={handleClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="d2fa-body">

          {/* Warning card */}
          <div className="d2fa-warning-card">
            <div className="d2fa-warning-icon-wrap">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <div className="d2fa-warning-content">
              <p className="d2fa-warning-title">Security Risk</p>
              <p className="d2fa-warning-desc">
                Disabling Two-Factor Authentication removes an important security layer from your account. Without 2FA, your account is significantly more vulnerable to unauthorised access.
              </p>
            </div>
          </div>

          {/* What will happen list */}
          <div className="d2fa-consequences">
            <p className="d2fa-consequences-title">What will happen:</p>
            <div className="d2fa-consequence-list">
              {[
                "2FA verification will no longer be required at login",
                "Your authenticator app will stop generating valid codes",
                "Your recovery key will be invalidated",
                "Your account will rely on password authentication only",
              ].map((item, i) => (
                <div key={i} className="d2fa-consequence-item">
                  <div className="d2fa-consequence-dot" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recommendation */}
          <div className="d2fa-tip">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>We strongly recommend keeping 2FA enabled to protect your account.</span>
          </div>

          {/* Confirm checkbox — matching DeleteOrgModal pattern */}
          <label className="d2fa-confirm-row">
            <input
              type="checkbox"
              className="d2fa-confirm-check"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <span className="d2fa-confirm-label">
              I understand the security risks and want to deactivate Two-Factor Authentication
            </span>
          </label>

        </div>

        {/* Footer */}
        <div className="d2fa-footer">
          <div className="d2fa-footer-note">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            Confirm your intent before proceeding
          </div>
          <div className="d2fa-footer-btns">
            <button className="d2fa-btn-cancel" onClick={handleClose}>Cancel</button>
            <button
              className={`d2fa-btn-deactivate${confirmed ? "" : " d2fa-btn-deactivate--disabled"}`}
              onClick={handleConfirm}
              disabled={!confirmed || isLoading}
            >
              {isLoading ? (
                <span className="d2fa-spinner" />
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                  </svg>
                  Deactivate 2FA
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .d2fa-panel {
          font-family: 'Plus Jakarta Sans', sans-serif;
          position: fixed; top: 0; right: 0; bottom: 0;
          width: 100%; max-width: 550px;
          background: #fff; z-index: 1004;
          display: flex; flex-direction: column;
          box-shadow: -8px 0 40px rgba(15,23,42,.15);
          transition: transform .32s cubic-bezier(0.16,1,0.3,1);
          overflow: hidden;
        }
        .d2fa-panel--open  { transform: translateX(0); }
        .d2fa-panel--close { transform: translateX(100%); }

        .d2fa-glow {
          height: 3px; flex-shrink: 0;
          background: linear-gradient(90deg,#7f1d1d,#dc2626,#ef4444,#dc2626,#7f1d1d);
          background-size: 200% 100%;
          animation: d2faGlow 3s linear infinite;
        }
        @keyframes d2faGlow { 0%{background-position:0%} 100%{background-position:200%} }

        .d2fa-header {
          padding: 15px 26px;
          background: linear-gradient(135deg,#7f1d1d 0%,#b91c1c 60%,#dc2626 100%);
          display: flex; align-items: center; justify-content: space-between; flex-shrink: 0;
        }
        .d2fa-header-left { display: flex; align-items: center; gap: 14px; }
        .d2fa-header-icon {
          width: 42px; height: 42px; border-radius: 11px;
          background: rgba(255,255,255,.18); border: 1.5px solid rgba(255,255,255,.28);
          display: flex; align-items: center; justify-content: center;
          color: #fff; flex-shrink: 0;
        }
        .d2fa-header-title { font-size: 16px; font-weight: 800; color: #fff; letter-spacing: -.3px; margin-bottom: 2px; line-height: 22px; }
        .d2fa-header-sub { font-size: 12.5px; color: rgba(255,255,255,.65); font-weight: 500; }
        .d2fa-close-btn {
          background: rgba(255,255,255,.15); border: none; border-radius: 8px;
          width: 32px; height: 32px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: rgba(255,255,255,.8); transition: background .15s;
        }
        .d2fa-close-btn:hover { background: rgba(255,255,255,.25); color: #fff; }

        .d2fa-body { flex: 1; padding: 26px; display: flex; flex-direction: column; gap: 18px; overflow-y: auto; }
        .d2fa-body::-webkit-scrollbar { width: 4px; }
        .d2fa-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }

        .d2fa-warning-card {
          display: flex; gap: 14px;
          background: linear-gradient(135deg,#fff7f7,#fee2e2);
          border: 1.5px solid #fecaca; border-radius: 14px; padding: 18px;
        }
        .d2fa-warning-icon-wrap {
          width: 44px; height: 44px; border-radius: 11px;
          background: #fff; border: 1.5px solid #fecaca;
          display: flex; align-items: center; justify-content: center;
          color: #dc2626; flex-shrink: 0;
        }
        .d2fa-warning-content { flex: 1; }
        .d2fa-warning-title { font-size: 14px; font-weight: 700; color: #991b1b; margin-bottom: 6px; line-height: 1.2; }
        .d2fa-warning-desc { font-size: 13px; color: #b91c1c; line-height: 1.6; font-weight: 500; }

        .d2fa-consequences { background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; }
        .d2fa-consequences-title { font-size: 11.5px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px; }
        .d2fa-consequence-list { display: flex; flex-direction: column; gap: 9px; }
        .d2fa-consequence-item { display: flex; align-items: center; gap: 10px; font-size: 13px; color: #475569; font-weight: 500; line-height: 1.4; }
        .d2fa-consequence-dot { width: 6px; height: 6px; border-radius: 50%; background: #ef4444; flex-shrink: 0; }

        .d2fa-tip {
          display: flex; align-items: flex-start; gap: 9px;
          background: #eff6ff; border: 1.5px solid #bfdbfe; border-radius: 10px;
          padding: 12px 14px; font-size: 12.5px; color: #1d4ed8; font-weight: 500; line-height: 1.5;
        }
        .d2fa-tip svg { flex-shrink: 0; margin-top: 1px; color: #2563eb; }

        .d2fa-confirm-row {
          display: flex; align-items: flex-start; gap: 12px; cursor: pointer;
          background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 12px; padding: 14px;
        }
        .d2fa-confirm-check { width: 18px; height: 18px; flex-shrink: 0; margin-top: 2px; accent-color: #dc2626; cursor: pointer; }
        .d2fa-confirm-label { font-size: 13px; font-weight: 600; color: #92400e; line-height: 1.55; }

        .d2fa-footer {
          padding: 14px 26px; border-top: 1px solid #f1f5f9; background: #fafbfc;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; flex-shrink: 0;
        }
        .d2fa-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .d2fa-footer-btns { display: flex; gap: 10px; }

        .d2fa-btn-cancel {
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 600; color: #64748b;
          padding: 10px 20px; border-radius: 10px; border: 1.5px solid #e2e8f0; background: #fff;
          cursor: pointer; transition: all .15s;
        }
        .d2fa-btn-cancel:hover { background: #f1f5f9; border-color: #cbd5e1; }

        .d2fa-btn-deactivate {
          display: inline-flex; align-items: center; gap: 7px;
          font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff;
          padding: 10px 20px; border-radius: 10px; border: none;
          background: linear-gradient(135deg,#991b1b,#dc2626);
          cursor: pointer; box-shadow: 0 4px 14px rgba(220,38,38,.28);
          transition: transform .15s, box-shadow .18s;
        }
        .d2fa-btn-deactivate:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(220,38,38,.36); }
        .d2fa-btn-deactivate--disabled { opacity: .42; cursor: not-allowed; transform: none !important; box-shadow: none !important; }

        .d2fa-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: d2faSpin .65s linear infinite; }
        @keyframes d2faSpin { to{transform:rotate(360deg)} }

        @media (max-width: 540px) {
          .d2fa-panel { max-width: 100%; }
          .d2fa-header, .d2fa-body, .d2fa-footer { padding-left: 18px; padding-right: 18px; }
        }
      `}</style>
    </Modal>
  );
};


/* ═══════════════════════════════════════════════
   Main TwoFactorAuthentication component
═══════════════════════════════════════════════ */
const TwoFactorAuthentication = ({
  status, data, profileData, fetch2fa, fetchProfile, model2FA, setModel2FA,
}: {
  status?: any; data?: any; fetch2fa?: () => void;
  profileData?: any; fetchProfile?: () => void;
  model2FA?: any; setModel2FA?: any;
}) => {
  const location = useLocation();
  const verifyData = data;
  const rootForm = { otp: "" };

  // Activation panel
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = React.useState<boolean>();
  const [copy, setCopy] = React.useState(false);
  const [copyKey, setCopyKey] = React.useState(false);
  const [formValuesErr, setFormValuesErr] = React.useState<any>(rootForm);
  const [authCode, setAuthCode] = React.useState(["", "", "", "", "", ""]);
  const [isCloseModal, setIsCloseModal] = React.useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>(new Array(6).fill(null));
  const navigate = useNavigate();

  // Deactivation confirmation panel
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);
  const [deactivateLoading, setDeactivateLoading] = React.useState(false);

  const handleClose = () => {
    setIsCloseModal(true);
    setFormValuesErr(rootForm);
    model2FA && setModel2FA("");
    setAuthCode(["", "", "", "", "", ""]);
    setTimeout(() => { setIsCloseModal(false); onClose(); }, 320);
  };

  useEffect(() => { if (status) onOpen(); }, [status, model2FA]);

  const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d$/.test(value) || value === "") {
      const newCode = [...authCode];
      newCode[index] = value;
      setAuthCode(newCode);
      if (value !== "" && index < 5) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && authCode[index] === "" && index > 0) inputRefs.current[index - 1]?.focus();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pastedData)) { setAuthCode(pastedData.split("")); inputRefs.current[5]?.focus(); }
  };

  function validateData() {
    const final_otp = authCode.join("");
    if (final_otp.length < 6) {
      setFormValuesErr((p: any) => ({ ...p, otp: "OTP must be 6 characters" }));
      return false;
    }
    return true;
  }

  async function handleSubmit(e: any) {
    e.preventDefault();
    const final_otp = authCode.join("");
    if (!final_otp) { toast.error("OTP is required"); return; }
    if (final_otp.length !== 6) { toast.error("OTP must be 6 characters"); return; }
    if (!validateData()) return;
    try {
      setIsLoading(true);
      await get2FTAuthVerify({
        otp: final_otp,
        user_id: verifyData?.user_id ?? profileData?.id,
        secret_key: verifyData?.secret_key,
        recover_key: model2FA ?? verifyData?.recover_key,
        inside_flag: model2FA ? true : false,
      }).then((d) => {
        if (d.status === "false") { toast.error(d.message); return; }
        if (model2FA) setToken(d.auth_token);
        get2FTAuth({
          enable_2FA: true,
          secret_key: verifyData?.secret_key,
          recover_key: model2FA ?? verifyData?.recover_key,
          user_id: verifyData?.user_id ?? profileData?.id,
        }).then(() => {
          handleClose();
          if (model2FA) navigate("/admin/dashboard");
          toast.success("Two-Factor Authentication Activated");
          if (location?.pathname !== "/auth/sign-in") fetchProfile?.();
        }).catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"));
      }).catch((err) => toast.error(err?.response?.data?.message ?? "Something went wrong"));
    } catch (err: any) {
      if (err?.response?.status === 422) toast.error("Please provide valid values");
    } finally {
      setIsLoading(false);
    }
  }

  const handleDeactivateConfirm = () => {
    setDeactivateLoading(true);
    try {
      fetch2fa?.();
      setDeactivateOpen(false);
    } finally {
      setDeactivateLoading(false);
    }
  };

  const copyToClipboard = (text: string, setter: (v: boolean) => void) => {
    navigator.clipboard.writeText(text);
    setter(true);
    setTimeout(() => setter(false), 1500);
  };

  return (
    <>
      {/* ── Trigger buttons ── */}
      {status ? null : !profileData?.auth_2fa ? (
        <button onClick={onOpen} className="tfa-trigger-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Activate 2FA
        </button>
      ) : (
        <button onClick={() => setDeactivateOpen(true)} className="tfa-trigger-btn tfa-trigger-btn--danger">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Deactivate 2FA
        </button>
      )}

      {/* ── Deactivate confirmation panel ── */}
      <Deactivate2FAPanel
        isOpen={deactivateOpen}
        onClose={() => setDeactivateOpen(false)}
        onConfirm={handleDeactivateConfirm}
        isLoading={deactivateLoading}
      />

      {/* ── Activation side panel ── */}
      <Modal isOpen={isOpen} onClose={() => {}}>
        <ModalOverlay bg="rgba(15,23,42,0.55)" backdropFilter="blur(4px)" />

        <div className={`tfa2-panel${isOpen && !isCloseModal ? " tfa2-panel--open" : " tfa2-panel--close"}`}>
          <div className="tfa2-glow" />

          <div className="tfa2-header">
            <div className="tfa2-header-brand">
              <div className="tfa2-header-logo">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
              </div>
              <div>
                <h2 className="tfa2-title">Two-Factor Authentication</h2>
                <p className="tfa2-sub">Secure your account with 2FA</p>
              </div>
            </div>
            <button className="tfa2-close" onClick={handleClose} aria-label="Close">
              <MdHighlightOff size={22} />
            </button>
          </div>

          <div className="tfa2-body">
            {/* Section 1 */}
            <div className="tfa2-section">
              <div className="tfa2-section-header">
                <div className="tfa2-section-num">1</div>
                <h3 className="tfa2-section-title">Configure your authenticator app</h3>
              </div>
              <div className="tfa2-app-row">
                {[
                  { name: "Google Authenticator", platforms: "iOS · Android" },
                  { name: "Authy", platforms: "iOS · Android · Desktop" },
                ].map((app) => (
                  <div key={app.name} className="tfa2-app-card">
                    <div className="tfa2-app-icon">
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                      </svg>
                    </div>
                    <div>
                      <p className="tfa2-app-name">{app.name}</p>
                      <p className="tfa2-app-platforms">{app.platforms}</p>
                    </div>
                  </div>
                ))}
              </div>
              <ol className="tfa2-steps-list">
                <li>Install Google Authenticator or Authy on your device.</li>
                <li>Open the app and tap the <strong>+</strong> icon to add a new account.</li>
                <li>Choose <em>"Scan a QR code"</em> and point your camera below.</li>
              </ol>
            </div>

            {/* Section 2 */}
            <div className="tfa2-section">
              <div className="tfa2-section-header">
                <div className="tfa2-section-num">2</div>
                <h3 className="tfa2-section-title">Scan QR Code</h3>
              </div>
              <div className="tfa2-qr-wrap">
                <div className="tfa2-qr-frame">
                  <img src={`data:image/png;base64 , ${data?.base64_image}`} alt="QR Code for 2FA" className="tfa2-qr-img" />
                </div>
                <div className="tfa2-qr-meta">
                  <p className="tfa2-qr-hint">Point your authenticator app at this QR code to link your account.</p>
                  <div className="tfa2-key-row">
                    <p className="tfa2-key-label">Secret Key</p>
                    <div className="tfa2-key-value-row">
                      <code className="tfa2-key-code">{data?.secret_key}</code>
                      <button className={`tfa2-copy-btn${copyKey ? " tfa2-copy-btn--copied" : ""}`} onClick={() => copyToClipboard(data?.secret_key, setCopyKey)}>
                        {copyKey ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</> : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>}
                      </button>
                    </div>
                  </div>
                  {!model2FA && (
                    <div className="tfa2-key-row">
                      <p className="tfa2-key-label">Recovery Key<span className="tfa2-key-badge">Save this!</span></p>
                      <div className="tfa2-key-value-row">
                        <code className="tfa2-key-code">{data?.recover_key}</code>
                        <button className={`tfa2-copy-btn${copy ? " tfa2-copy-btn--copied" : ""}`} onClick={() => copyToClipboard(data?.recover_key, setCopy)}>
                          {copy ? <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Copied</> : <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy</>}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="tfa2-section tfa2-section--last">
              <div className="tfa2-section-header">
                <div className="tfa2-section-num tfa2-section-num--active">3</div>
                <h3 className="tfa2-section-title">Verify the code</h3>
              </div>
              <p className="tfa2-verify-hint">Enter the 6-digit code currently shown in your authenticator app.</p>
              <div className="tfa2-otp-row">
                {authCode.map((digit, index) => (
                  <input key={index} ref={(el) => (inputRefs.current[index] = el)}
                    type="text" inputMode="numeric" maxLength={1} value={digit}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className={`tfa2-otp-box${digit ? " tfa2-otp-box--filled" : ""}`}
                    aria-label={`Digit ${index + 1}`}
                  />
                ))}
              </div>
              {formValuesErr.otp && (
                <p className="tfa2-otp-err">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {formValuesErr.otp}
                </p>
              )}
            </div>
          </div>

          <div className="tfa2-footer">
            <div className="tfa2-footer-note">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              2FA adds a critical extra layer of security
            </div>
            <div className="tfa2-footer-btns">
              <button className="tfa2-btn-cancel" onClick={handleClose}>Cancel</button>
              <button className="tfa2-btn-verify" onClick={handleSubmit} disabled={!!isLoading}>
                {isLoading ? <span className="tfa2-spinner" /> : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>Verify &amp; Activate</>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

        .tfa-trigger-btn { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:700; padding:9px 16px; border-radius:10px; cursor:pointer; border:1.5px solid #bfdbfe; background:#eff6ff; color:#2563eb; transition:all .18s; }
        .tfa-trigger-btn:hover { background:#dbeafe; border-color:#93c5fd; }
        .tfa-trigger-btn--danger { border-color:#fecaca; background:#fff7f7; color:#dc2626; }
        .tfa-trigger-btn--danger:hover { background:#fee2e2; border-color:#fca5a5; }

        .tfa2-panel { font-family:'Plus Jakarta Sans',sans-serif; position:fixed; top:0; right:0; bottom:0; width:100%; max-width:580px; background:#fff; z-index:1004; display:flex; flex-direction:column; box-shadow:-12px 0 60px rgba(15,23,42,.18); transition:transform .35s cubic-bezier(0.16,1,0.3,1); overflow:hidden; }
        .tfa2-panel--open  { transform:translateX(0); }
        .tfa2-panel--close { transform:translateX(100%); }

        .tfa2-glow { height:3px; flex-shrink:0; background:linear-gradient(90deg,#0f2744,#2563eb,#7c3aed,#2563eb,#0f2744); background-size:200% 100%; animation:tfa2Glow 3s linear infinite; }
        @keyframes tfa2Glow { 0%{background-position:0%} 100%{background-position:200%} }

        .tfa2-header { padding:15px 24px; background:linear-gradient(135deg,#0f2744 0%,#1d4ed8 60%,#2563eb 100%); display:flex; align-items:center; justify-content:space-between; flex-shrink:0; }
        .tfa2-header-brand { display:flex; align-items:center; gap:13px; }
        .tfa2-header-logo { width:42px; height:42px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .tfa2-title { font-size:16px; font-weight:800; color:#fff; letter-spacing:-.3px; margin-bottom:2px; line-height:1; }
        .tfa2-sub { font-size:12px; color:rgba(255,255,255,.55); font-weight:500; }
        .tfa2-close { background:none; border:none; cursor:pointer; color:rgba(255,255,255,.65); display:flex; align-items:center; transition:color .15s; padding:2px; }
        .tfa2-close:hover { color:#fff; }

        .tfa2-body { flex:1; overflow-y:auto; overflow-x:hidden; padding:0 24px; }
        .tfa2-body::-webkit-scrollbar { width:4px; }
        .tfa2-body::-webkit-scrollbar-thumb { background:#cbd5e1; border-radius:4px; }

        .tfa2-section { padding:20px 0; border-bottom:1px solid #f1f5f9; }
        .tfa2-section--last { border-bottom:none; padding-bottom:10px; }
        .tfa2-section-header { display:flex; align-items:center; gap:10px; margin-bottom:14px; }
        .tfa2-section-num { width:26px; height:26px; border-radius:50%; background:#f1f5f9; color:#64748b; font-size:12px; font-weight:700; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
        .tfa2-section-num--active { background:#eff6ff; color:#2563eb; }
        .tfa2-section-title { font-size:13.5px; font-weight:700; color:#1e293b; }

        .tfa2-app-row { display:flex; gap:10px; margin-bottom:14px; flex-wrap:wrap; }
        .tfa2-app-card { display:flex; align-items:center; gap:10px; background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:11px; padding:10px 14px; flex:1; min-width:150px; transition:border-color .15s; }
        .tfa2-app-card:hover { border-color:#93c5fd; }
        .tfa2-app-icon { width:34px; height:34px; border-radius:9px; background:linear-gradient(135deg,#eff6ff,#dbeafe); display:flex; align-items:center; justify-content:center; color:#2563eb; flex-shrink:0; }
        .tfa2-app-name { font-size:12.5px; font-weight:700; color:#0f172a; margin-bottom:2px; }
        .tfa2-app-platforms { font-size:11px; color:#94a3b8; font-weight:500; }

        .tfa2-steps-list { list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:6px; }
        .tfa2-steps-list li { font-size:12.5px; color:#475569; line-height:1.5; display:flex; gap:8px; }
        .tfa2-steps-list li::before { content:'→'; color:#93c5fd; font-size:12px; margin-top:1px; flex-shrink:0; }

        .tfa2-qr-wrap { display:flex; gap:18px; align-items:flex-start; flex-wrap:wrap; }
        .tfa2-qr-frame { background:#fff; border:2px solid #e2e8f0; border-radius:13px; padding:10px; flex-shrink:0; box-shadow:0 2px 10px rgba(0,0,0,.06); }
        .tfa2-qr-img { width:130px; height:130px; display:block; }
        .tfa2-qr-meta { flex:1; min-width:0; display:flex; flex-direction:column; gap:12px; }
        .tfa2-qr-hint { font-size:12.5px; color:#64748b; line-height:1.55; }

        .tfa2-key-row { display:flex; flex-direction:column; gap:5px; }
        .tfa2-key-label { display:flex; align-items:center; gap:6px; font-size:11px; font-weight:700; color:#374151; text-transform:uppercase; letter-spacing:0.4px; }
        .tfa2-key-badge { font-size:10px; font-weight:600; text-transform:none; background:#fef3c7; color:#92400e; border:1px solid #fde68a; border-radius:100px; padding:2px 8px; letter-spacing:0; }
        .tfa2-key-value-row { display:flex; align-items:center; gap:8px; background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:9px; padding:8px 12px; min-width:0; overflow:hidden; }
        .tfa2-key-code { font-family:'Courier New',monospace; font-size:12.5px; color:#1e293b; font-weight:600; flex:1 1 0%; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; letter-spacing:.5px; word-break:break-all; }
        .tfa2-copy-btn { display:inline-flex; align-items:center; gap:5px; font-family:'Plus Jakarta Sans',sans-serif; font-size:11.5px; font-weight:600; padding:5px 10px; border-radius:7px; border:1.5px solid #e2e8f0; background:#fff; color:#64748b; cursor:pointer; transition:all .15s; flex-shrink:0; }
        .tfa2-copy-btn:hover { border-color:#93c5fd; color:#2563eb; background:#eff6ff; }
        .tfa2-copy-btn--copied { border-color:#bbf7d0; color:#15803d; background:#f0fdf4; }

        .tfa2-verify-hint { font-size:12.5px; color:#64748b; margin-bottom:16px; line-height:1.5; }
        .tfa2-otp-row { display:flex; gap:10px; justify-content:center; margin-bottom:12px; }
        .tfa2-otp-box { width:54px; height:62px; background:#f8fafc; border:1.5px solid #e2e8f0; border-radius:14px; font-family:'Plus Jakarta Sans',sans-serif; font-size:24px; font-weight:700; color:#0f172a; text-align:center; outline:none; transition:border-color .18s, box-shadow .18s, background .18s; caret-color:#2563eb; }
        .tfa2-otp-box:focus { border-color:#2563eb; background:#fff; box-shadow:0 0 0 3px rgba(37,99,235,.1); }
        .tfa2-otp-box--filled { border-color:#93c5fd; background:#eff6ff; color:#1d4ed8; }
        .tfa2-otp-err { display:flex; align-items:center; justify-content:center; gap:5px; font-size:12px; color:#dc2626; font-weight:500; margin-top:4px; }

        .tfa2-footer { padding:14px 24px; border-top:1.5px solid #f1f5f9; background:#fafbfc; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:10px; flex-shrink:0; }
        .tfa2-footer-note { display:flex; align-items:center; gap:6px; font-size:12px; color:#94a3b8; font-weight:500; }
        .tfa2-footer-btns { display:flex; align-items:center; gap:10px; }
        .tfa2-btn-cancel { font-family:'Plus Jakarta Sans',sans-serif; font-size:13px; font-weight:600; color:#64748b; padding:9px 16px; border-radius:10px; border:1.5px solid #e2e8f0; background:#fff; cursor:pointer; transition:all .15s; }
        .tfa2-btn-cancel:hover { background:#f1f5f9; color:#374151; }
        .tfa2-btn-verify { display:inline-flex; align-items:center; gap:7px; font-family:'Plus Jakarta Sans',sans-serif; font-size:13.5px; font-weight:700; color:#fff; padding:10px 20px; border-radius:10px; border:none; background:linear-gradient(135deg,#1d4ed8,#2563eb); cursor:pointer; box-shadow:0 4px 14px rgba(37,99,235,.3); transition:transform .15s, box-shadow .18s; position:relative; overflow:hidden; }
        .tfa2-btn-verify::after { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,.1),transparent 60%); }
        .tfa2-btn-verify:hover { transform:translateY(-1px); box-shadow:0 8px 24px rgba(37,99,235,.38); }
        .tfa2-btn-verify:disabled { opacity:.6; cursor:not-allowed; transform:none; }
        .tfa2-spinner { display:inline-block; width:16px; height:16px; border:2px solid rgba(255,255,255,.35); border-top-color:#fff; border-radius:50%; animation:tfa2Spin .65s linear infinite; }
        @keyframes tfa2Spin { to{transform:rotate(360deg)} }

        @media (max-width:600px) {
          .tfa2-panel { max-width:100%; }
          .tfa2-header, .tfa2-footer { padding-left:18px; padding-right:18px; }
          .tfa2-body { padding:0 18px; }
          .tfa2-qr-wrap { flex-direction:column; align-items:stretch; }
          .tfa2-qr-frame { align-self:center; }
          .tfa2-qr-meta { min-width:0; width:100%; }
          .tfa2-key-value-row { flex-wrap:nowrap; }
          .tfa2-otp-box { width:44px; height:52px; font-size:20px; }
          .tfa2-otp-row { gap:7px; }
          .tfa2-footer-btns { width:100%; justify-content:flex-end; }
          .tfa2-footer-note { display:none; }
        }
        @media (max-width:420px) {
          /* Key row: shrink copy button to icon only */
          .tfa2-copy-btn span { display:none; }
          .tfa2-copy-btn { padding:5px 7px; gap:0; }
          .tfa2-key-code { font-size:11px; letter-spacing:0.2px; }
          .tfa2-key-value-row { padding:7px 10px; }
        }
        @media (max-width:380px) {
          .tfa2-otp-box { width:38px; height:46px; font-size:17px; border-radius:10px; }
          .tfa2-otp-row { gap:5px; }
          .tfa2-key-code { font-size:10.5px; }
        }
      `}</style>
    </>
  );
};

export default TwoFactorAuthentication;